// ============================================================================
// TeamBridge Reporting — Command Center (server-driven hybrid)
//
// STATS ARE GLOBAL: exec summary, KPIs, funnel, breakdowns, SLA and partner
// scorecard come from the RPT aggregate endpoints, which compute over the
// ENTIRE database (SQL), filter-aware via query params (Mule now forwards).
// THE GRID IS WINDOWED: true server-side pagination (50/page over the full
// dataset) + server-side sort/search/filters. Nothing is limited to a
// client-side sample anymore.
// ============================================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppShell from "../layout/AppShell";
import {
    rptSummary, rptFunnel, rptBreakdown, rptRequests, rptRequestDetail,
    rptFailures, rptAppCoverage, rptPartners,
} from "../api/rptClient";
import "./ReportingDashboard.css";

// ---- status-category maps (canonical model) ---------------------------------
const CAT_COLOR = { TERMINAL_SUCCESS: "#34d399", IN_FLIGHT: "#60a5fa", ON_HOLD: "#fbbf24", TERMINAL_FAILURE: "#f87171", CLOSED_OTHER: "#9aa7c4", UNKNOWN: "#6b7ba6", UNMAPPED: "#a78bfa" };
const CAT_LABEL = { TERMINAL_SUCCESS: "Completed", IN_FLIGHT: "In flight", ON_HOLD: "On hold", TERMINAL_FAILURE: "Failed", CLOSED_OTHER: "Other", UNKNOWN: "Unmapped", UNMAPPED: "Unmapped" };
const SC = { Done: "#34d399", Provisioning: "#60a5fa", Pending: "#fbbf24", Issue: "#f87171", Mentioned: "#6b7ba6" };
const AV = ["#0076c0", "#0f9d6b", "#7c5cd6", "#c98300", "#0891b2", "#dc3a3a", "#2563eb"];
const PAGE_SIZE = 50;

// ---- helpers -----------------------------------------------------------------
const initials = (n) => (n || "?").split(" ").map((x) => x[0]).join("").slice(0, 2).toUpperCase();
const hsh = (s) => { let h = 0; s = s || "?"; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return h; };
const mk = (masked, v) => (!masked || !v) ? (v || "") : (String(v).length <= 3 ? "•••" : String(v).slice(0, 3) + "•••");
const mkn = (masked, v) => (!masked || !v) ? (v || "") : String(v).split(" ").map((w) => (w ? w[0] + "•••" : "")).join(" ");
const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);
// "7/8/2026 4:00 PM" — the reference-dashboard date style, instead of raw ISO.
const fmtDt = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    let h = d.getHours(); const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12;
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()} ${h}:${String(d.getMinutes()).padStart(2, "0")} ${ap}`;
};
const nm = (r) => [r.first_name, r.last_name].filter(Boolean).join(" ");

// Partner grouping now comes from the server: GET /api/partners resolves each
// request to its partner via the dim_store_partner crosswalk (store_code =
// org_unit_code), falling back to the vendor string. The old client-side
// corpOf() heuristic has been removed.

// Rejected / cancelled requests live in the neutral CLOSED_OTHER category, which
// the aggregate views group under "Other". In the per-record drawer and the grid
// we want the specific outcome instead — "Rejected", "Cancelled" — and the
// sub-system provisioning rows shown as REJECTED rather than a misleading
// PENDING (nothing will ever provision on a closed request). Data is unchanged;
// this is display-only.
const REJECTED_LIKE = "CLOSED_OTHER";
const prettyStatus = (code) => String(code || "").toLowerCase().split(/[_\s]+/).filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");
const stageLabel = (row) => (row.status_category === REJECTED_LIKE && (row.status || row.request_status))
    ? prettyStatus(row.status || row.request_status)
    : (CAT_LABEL[row.status_category] || row.status || row.request_status || "—");

function useDebounced(value, ms) {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
    return v;
}

function Legend({ items }) {
    return (
        <div className="leg">
            {items.map(([c, t]) => <span key={t}><i style={{ background: c }} />{t}</span>)}
        </div>
    );
}
function Avatar({ name, size }) {
    const st = { background: AV[hsh(name) % AV.length] };
    if (size) { st.width = size; st.height = size; st.fontSize = 15; }
    return <span className="av" style={st}>{initials(name)}</span>;
}
function Kpi({ l, v, s, vc, ico, chip, cc, onClick }) {
    return (
        <div className={"kcard" + (onClick ? " clickable" : "")}
            onClick={onClick}
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
            title={onClick ? "View these in the Agent Grid" : undefined}
            onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}>
            <span className="kc-ico">{ico || ""}</span>
            <span className="kc-l">{l}</span>
            <span className="kc-v" style={vc ? { color: vc } : undefined}>{v}</span>
            <div className="kc-row" style={{ flexWrap: "wrap" }}>
                {chip && <span className="kc-chip" style={{ background: (cc || "#0f9d6b") + "22", color: cc || "#0f9d6b", whiteSpace: "normal", lineHeight: 1.35, maxWidth: "100%" }}>{chip}</span>}
                <span className="kc-s">{s}</span>
            </div>
        </div>
    );
}
function Donut({ counts, colors, labels }) {
    const entries = Object.entries(counts).filter(([, n]) => n > 0);
    const total = entries.reduce((a, [, n]) => a + n, 0) || 1;
    let acc = 0;
    const stops = entries.map(([k, n]) => {
        const from = (acc / total) * 100; acc += n;
        return `${colors[k] || "#6b7ba6"} ${from}% ${(acc / total) * 100}%`;
    }).join(",");
    return (
        <div className="donutwrap">
            <div className="donut" style={{ background: entries.length ? `conic-gradient(${stops})` : "var(--soft)" }} />
            <div className="legend">
                {entries.map(([k, n]) => (
                    <div key={k}><i style={{ background: colors[k] || "#6b7ba6" }} />{(labels && labels[k]) || k}<b>{n.toLocaleString()}</b></div>
                ))}
            </div>
        </div>
    );
}
/** Vertical stacked columns with y-axis + gridlines (reference Chart.js look). */
/** Compact progress meter for the grid's app columns: colored bar + n/total.
 *  Palettes: DTV = brand blue, ATT = cyan→teal; 100% flips to green, 0% shows
 *  an amber sliver on the empty track so "nothing done" is scannable. */
function AppMeter({ done, total, palette = "DTV" }) {
    if (!total) return <span style={{ color: "var(--faint)" }}>—</span>;
    const pctW = Math.round((done / total) * 100);
    const full = done >= total;
    const fill = full ? "linear-gradient(90deg,#10b981,#34d399)"
        : palette === "ATT" ? "linear-gradient(90deg,#00a8e0,#34d399)"
            : "linear-gradient(90deg,#0076c0,#3b9ae1)";
    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }} title={`${done} of ${total} provisioned`}>
            <span style={{ width: 54, height: 9, borderRadius: 5, background: done === 0 ? "#f4c15022" : "var(--soft)", overflow: "hidden", display: "inline-block", boxShadow: done === 0 ? "inset 0 0 0 1px #f4c15055" : "inset 0 0 0 1px var(--line)" }}>
                <span style={{ display: "block", height: "100%", width: `${pctW}%`, background: fill, borderRadius: 5 }} />
            </span>
            <span className="mono" style={{ fontSize: 11, fontWeight: 700, color: full ? "#0f9d6b" : done === 0 ? "#c98300" : "var(--dim)" }}>{done}/{total}</span>
        </span>
    );
}

function VBars({ rows, height = 240 }) {
    if (!rows.length) return <div className="note">No data for the current filters.</div>;
    const W = Math.max(420, rows.length * 74), H = height, padL = 44, padB = 40, padT = 10;
    const max = Math.max(...rows.map((r) => r.total), 1);
    const step = Math.pow(10, Math.floor(Math.log10(max)));
    const niceMax = Math.ceil(max / step) * step;
    const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(niceMax * f));
    const y = (v) => padT + (H - padT - padB) * (1 - v / niceMax);
    const bw = Math.min(46, (W - padL) / rows.length * 0.55);
    return (
        <div className="vbwrap">
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>
                {ticks.map((t) => (
                    <g key={t}>
                        <line x1={padL} x2={W - 6} y1={y(t)} y2={y(t)} stroke="var(--line)" strokeDasharray={t ? "3 4" : ""} strokeWidth="1" />
                        <text x={padL - 7} y={y(t) + 3.5} textAnchor="end" fontSize="10" fill="var(--faint)">{t.toLocaleString()}</text>
                    </g>
                ))}
                {rows.map((r, i) => {
                    const cx = padL + ((W - padL) / rows.length) * (i + 0.5);
                    let acc = 0;
                    return (
                        <g key={r.label}>
                            {r.segs.map((sg, j) => {
                                if (!sg.n) return null;
                                const y1 = y(acc + sg.n), y2 = y(acc); acc += sg.n;
                                return <rect key={j} x={cx - bw / 2} y={y1} width={bw} height={Math.max(1, y2 - y1)} fill={sg.color} rx="2"><title>{`${r.label} — ${sg.t}: ${sg.n.toLocaleString()}`}</title></rect>;
                            })}
                            <text x={cx} y={H - padB + 14} textAnchor="middle" fontSize="10.5" fill="var(--dim)"
                                transform={rows.length > 6 ? `rotate(-28 ${cx} ${H - padB + 14})` : undefined}>{r.label.length > 13 ? r.label.slice(0, 12) + "…" : r.label}</text>
                            <text x={cx} y={y(r.total) - 4} textAnchor="middle" fontSize="9.5" fontWeight="700" fill={r.topColor || "var(--faint)"}>
                                <title>{r.title || r.label}</title>
                                {r.top ?? r.total.toLocaleString()}
                            </text>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
}
/** rows: [{label,total,segs:[{n,color,t}]}] */
function HBars({ rows }) {
    const max = Math.max(...rows.map((r) => r.total), 1);
    return (
        <div className="hbars">
            {rows.map((r) => (
                <div className="hb" key={r.label}>
                    <div className="hl" title={r.label}>{r.label}</div>
                    <div className="hbar">
                        {r.segs.map((s, i) => s.n > 0 ? (
                            <i key={i} style={{ width: `${(s.n / max) * 100}%`, background: s.color }} title={`${s.t}: ${s.n}`} />
                        ) : null)}
                    </div>
                    <div className="hn">{r.total.toLocaleString()}</div>
                </div>
            ))}
            {!rows.length && <div className="note">No data for the current filters.</div>}
        </div>
    );
}

/** Completion leaderboard: full-width composition bar per partner (green share =
 *  % completed), so it stays informative even when a filtered slice is 0% done.
 *  Reuses the .hbars styles; segments are proportional to each partner's own total. */
function PartnerLeaderboard({ rows }) {
    return (
        <div className="hbars">
            {rows.map((r) => {
                const denom = r.total || 1;
                return (
                    <div className="hb" key={r.label}>
                        <div className="hl" title={`${r.label} · ${r.total.toLocaleString()} requests`}>{r.label}</div>
                        <div className="hbar">
                            {r.segs.map((s, i) => s.n > 0 ? (
                                <i key={i} style={{ width: `${(s.n / denom) * 100}%`, background: s.color }} title={`${s.t}: ${s.n}`} />
                            ) : null)}
                        </div>
                        <div className="hn">{r.pctDone}%</div>
                    </div>
                );
            })}
            {!rows.length && <div className="note">No partner data for this filter.</div>}
        </div>
    );
}

/** Drillable partner scorecard: master-corp bars that expand to their locations,
 *  and each location expands to its employees. Clicking an employee opens the
 *  same record drawer used by the agent grid. `metric` = "pct" (green share of
 *  that row's own total) or "total" (share of the largest row). Expand state is
 *  owned by the parent and shared across both panels, so a corp opened in one
 *  reflects in the other. Employees are lazy-loaded per location via /requests. */
function PartnerScorecard({ rows, metric, openCorp, openLoc, onToggleCorp, onToggleLoc, employees, onOpenEmployee, masked }) {
    if (!rows.length) return <div className="note">No partner data for this filter.</div>;
    const max = Math.max(...rows.map((r) => r.total), 1);
    const bar = (segs, denom, isTotal) => segs.map((s, i) => s.n > 0 ? (
        <i key={i} style={{ width: `${(isTotal ? s.n / max : s.n / (denom || 1)) * 100}%`, background: s.color }} title={`${s.t}: ${s.n.toLocaleString()}`} />
    ) : null);
    return (
        <div className="hbars">
            {rows.map((r) => {
                const on = openCorp === r.label;
                return (
                    <React.Fragment key={r.label}>
                        <div className={"hb corprow" + (on ? " on" : "")} onClick={() => onToggleCorp(r.label)}
                            title={`${r.label} · ${r.total.toLocaleString()} requests · ${r.locations.length} location(s)`}>
                            <div className="hl"><span className="caret">{on ? "▾" : "▸"}</span> {r.label}</div>
                            <div className="hbar">{bar(r.segs, r.total, metric === "total")}</div>
                            <div className="hn">{metric === "total" ? r.total.toLocaleString() : r.pctDone + "%"}</div>
                        </div>
                        {on && (
                            <div className="corpdrill">
                                {r.locations.map((loc) => {
                                    const lkey = loc.code ?? loc.label;
                                    const lon = openLoc === lkey;
                                    const emps = employees[lkey];
                                    return (
                                        <React.Fragment key={lkey}>
                                            <div className={"hb locrow" + (lon ? " on" : "")} onClick={() => onToggleLoc(lkey, loc.code != null)}
                                                title={`${loc.label} · ${loc.total.toLocaleString()} requests`}>
                                                <div className="hl"><span className="caret">{lon ? "▾" : "▸"}</span> {loc.label}</div>
                                                <div className="hbar">{bar(loc.segs, loc.total, false)}</div>
                                                <div className="hn">{loc.pctDone}%</div>
                                            </div>
                                            {lon && (
                                                <div className="appworkers">
                                                    {!emps && <div className="note">Loading employees…</div>}
                                                    {emps && (emps.length ? (
                                                        <table>
                                                            <thead><tr><th>Employee</th><th>DTVID</th><th>ATTUID</th><th>Stage</th><th>Type</th></tr></thead>
                                                            <tbody>{emps.map((w) => (
                                                                <tr key={w.source + w.key} className="clickrow" onClick={() => onOpenEmployee(w)}>
                                                                    <td>{mkn(masked, nm(w)) || w.key}</td>
                                                                    <td className="mono">{mk(masked, w.dtvid)}</td>
                                                                    <td className="mono">{mk(masked, w.attuid)}</td>
                                                                    <td><span className="badge" style={{ background: (CAT_COLOR[w.status_category] || "#6b7ba6") + "22", color: CAT_COLOR[w.status_category] || "#6b7ba6" }} title={w.status}>{CAT_LABEL[w.status_category] || w.status}</span></td>
                                                                    <td>{w.request_type}</td>
                                                                </tr>
                                                            ))}</tbody>
                                                        </table>
                                                    ) : <div className="note">No employees for this location.</div>)}
                                                </div>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

const TITLES = {
    overview: ["Onboarding overview", "application-level tracking"],
    agents: ["Agent grid", "full dataset · server-side search & paging"],
    risk: ["At-risk / SLA", "open provisioning failures across all systems"],
    vendors: ["Partner scorecard", "completion and throughput by partner"],
    apps: ["Application coverage", "from ServiceNow work notes"],
    arch: ["Data architecture", "how the data comes together"],
};
// ---------------------------------------------------------------------------
// SNS portal deep-link. Pattern (from the business team):
//   {base}/s/sns-agent-request/{salesforceRecordId}/{snsri slug}
//   e.g. https://directv-dmp.my.site.com/SNS/s/sns-agent-request/a32PY00000LvpZZYAZ/snsri00013173
// salesforceRecordId = the report's "SNS AGENT REQUEST: ID" column, which the
// SNS loader stores as source_record_id (already returned by /requests).
// slug = the SNSRI key lowercased with non-alphanumerics stripped.
// Base URL from env (VITE_SNS_PORTAL_BASE / REACT_APP_SNS_PORTAL_BASE) so
// test/prod portals can differ without a code change.
// ---------------------------------------------------------------------------
const readEnvVar = (key, fallback = "") => {
    try {
        // eslint-disable-next-line no-undef
        if (typeof import.meta !== "undefined" && import.meta?.env && import.meta.env[`VITE_${key}`] != null) return String(import.meta.env[`VITE_${key}`]);
    } catch { /* not vite */ }
    try {
        if (typeof process !== "undefined" && process?.env && process.env[`REACT_APP_${key}`] != null) return String(process.env[`REACT_APP_${key}`]);
    } catch { /* not cra */ }
    return fallback;
};
const SNS_PORTAL_BASE = readEnvVar("SNS_PORTAL_BASE", "https://directv-dmp.my.site.com/SNS").replace(/\/+$/, "");
const snsPortalUrl = (row) => {
    if (String(row?.source || "").toUpperCase() !== "SNS") return null;
    const sfid = String(row?.source_record_id || "").trim();
    const key = String(row?.key || "").trim();
    if (!sfid || !key) return null; // older rows without the Salesforce id: no link, plain text
    return `${SNS_PORTAL_BASE}/s/sns-agent-request/${encodeURIComponent(sfid)}/${key.toLowerCase().replace(/[^a-z0-9]/g, "")}`;
};

const SORTABLE = { name: 1, vendor: 1, status: 1, key: 1, submitted_at: 1, updated_at: 1, type: 1, dtv_apps: 1, att_apps: 1, snow: 1 };

// ---- rich person drawer (mirrors the reference agent-mapping detail view) ----
// Renders identity, per-app provisioning (DTV/Saviynt + AT&T/myLogins), an
// onboarding journey, per-system outcomes, and a work-note timeline.
// Reads `detail` defensively: each section appears only when its data exists,
// so it renders cleanly from `row` alone before `detail` loads, then enriches.
// CONFIRM the three array field names below against your rptRequestDetail
// response and adjust if your API names them differently.
// ---- work-note presentation helpers (timeline cards in the drawer) ----------
// Classify a note into a display status from its text (same vocabulary the
// SNOW loader uses), and highlight the outcome keywords inline: green for
// done-ish, amber for in-progress-ish, red for issues.
const NOTE_GOOD_RE = /(?:provisioned correctly|completed|complete\b|\bdone\b|granted|added|already has access|has access|\bactive\b)/i;
const NOTE_WARN_RE = /(?:still in progress|in progress|pending|waiting|requested|in queue|working on|to complete|not yet)/i;
const NOTE_BAD_RE = /(fail|error|unable|cannot|can't|locked|denied|issue)/i;
const noteStatusOf = (t) => {
    const x = String(t || "");
    if (NOTE_BAD_RE.test(x)) return { label: "ISSUE", color: "#dc3a3a" };
    if (NOTE_GOOD_RE.test(x)) return { label: "COMPLETED", color: "#0f9d6b" };
    if (NOTE_WARN_RE.test(x)) return { label: "IN PROGRESS", color: "#c98300" };
    return { label: "UPDATE", color: "#8593b4" };
};
// Strip the journal-type prefix line ("(Comments)" / "(Work notes)") — it is
// parser noise, not content.
const cleanNote = (t) => String(t || "").replace(/^\s*\((?:comments|work notes?)\)\s*/i, "").trim();
// Split on the outcome keywords and bold-color the matches.
const NOTE_HL_RE = new RegExp(`(${NOTE_GOOD_RE.source}|${NOTE_WARN_RE.source})`, "gi");
const highlightNote = (t) => String(t || "").split(NOTE_HL_RE).filter((p) => p !== undefined && p !== "").map((part, i) => {
    if (NOTE_GOOD_RE.test(part) && part.length <= 40 && new RegExp(`^(?:${NOTE_GOOD_RE.source})$`, "i").test(part))
        return <b key={i} style={{ color: "#0f9d6b" }}>{part}</b>;
    if (NOTE_WARN_RE.test(part) && part.length <= 40 && new RegExp(`^(?:${NOTE_WARN_RE.source})$`, "i").test(part))
        return <b key={i} style={{ color: "#c98300" }}>{part}</b>;
    return <React.Fragment key={i}>{part}</React.Fragment>;
});

function DrawerBody({ row, detail, masked }) {
    const D = detail || {};
    const apps = D.apps || D.app_access || [];
    const outcomes = D.outcomes || D.provisioning || [];
    const events = D.events || D.timeline || D.status_events || [];
    const notes = D.notes || [];                                      // ServiceNow work-note timeline

    const provOf = (a) => a.provider || (a.id_type === "ATTUID" ? "ATT" : "DTV");
    const statOf = (a) => a.note_status || a.access_status || "Mentioned";
    const isDone = (a) => String(a.note_status || "").toLowerCase() === "done" || a.access_status === "APPROVED";
    const dtv = apps.filter((a) => provOf(a) === "DTV");
    const att = apps.filter((a) => provOf(a) === "ATT");
    const dDone = dtv.filter(isDone).length, aDone = att.filter(isDone).length;

    // Failure detection for this record: a failed/retrying provisioning outcome,
    // or an app note flagged as an Issue. Drives both the error line under S&S
    // and the "no ticket => completed" behavior below.
    const failedOutcomes = outcomes.filter((o) => o.outcome_status === "FAILED" || o.outcome_status === "RETRYING");
    // Source-reported messages: failure text the loaders classified as benign or
    // not-required (e.g. CP "TERMINATE requires agentId … nothing to deprovision",
    // attuid_required=false) ends up on an NA/SUCCESS outcome with NO
    // error_message — it survives only in the raw staging row. Surface those here
    // as informational context so a "Failed" source status is never unexplained.
    // Deduped against messages already rendered on the outcomes themselves.
    const rawRec = D.rawRecord || D.raw_record || null;
    const outcomeMsgs = new Set(outcomes.map((o) => String(o.error_message || "").trim().toLowerCase()).filter(Boolean));
    const sourceMsgs = !rawRec ? [] : [
        ["dtv_failure_message", "SAVIYNT"],
        ["cp_failure_message", "CINGLEPOINT"],
        ["hinge_failure_message", "HINGE"],
        ["roma_error_message", "ROMA"],
    ].map(([f, label]) => ({ label, text: String(rawRec[f] || "").trim() }))
        .filter((m) => m.text && !outcomeMsgs.has(m.text.toLowerCase()));
    const appIssues = apps.filter((a) => String(a.note_status || "").toLowerCase() === "issue" || a.access_status === "REJECTED");
    const hasFailure = failedOutcomes.length > 0 || appIssues.length > 0;
    // "<TARGET>: <message>" per failed system, shown on the next line under S&S.
    const failMsg = failedOutcomes.map((o) => {
        const tgt = o.target || o.target_system_code || "";
        const msg = o.error_message || o.error_code || "failed";
        return tgt ? `${tgt}: ${msg}` : msg;
    }).join(" · ") || null;
    // No ServiceNow ticket + nothing failing => provisioning is treated as done.
    // NPW records frequently have no tickets; their absence must not read as pending.
    const dtvOk = dtv.length > 0 ? dDone >= dtv.length : !hasFailure;
    const attOk = att.length > 0 ? aDone >= att.length : !hasFailure;
    const noTicketSub = hasFailure ? "no app notes" : "0 tickets \u00b7 no action needed";

    // Journey adapts to request type. Offboarding must never say "onboarded".
    const isOff = String(row.request_type || "").toUpperCase() === "OFFBOARDING";
    // Human-friendly source key: NPW id for TeamBridge (not the client_request_id
    // UUID), SNSRI for SNS (which is already the key). Real row.key is kept for
    // drill-down navigation.
    const displayKey = String(row.source || "").toUpperCase() === "TEAMBRIDGE"
        ? (row.source_record_id || row.key)
        : row.key;
    const snsUrl = snsPortalUrl(row); // null for TB / rows without source_record_id
    const appVerb = isOff ? "deprovisioned" : "provisioned";
    const termOk = row.status_category === "TERMINAL_SUCCESS";
    const termFail = hasFailure || row.status_category === "TERMINAL_FAILURE";
    const termClosed = row.status_category === "CLOSED_OTHER"; // rejected / cancelled — closed, neutral
    // CINGLEPOINT N/A rule: when the NPW doesn't require an ATTUID, CP is NA and
    // the request counts complete once every other target (SAVIYNT/HINGE) is
    // SUCCESS — even if the canonical status hasn't caught up yet.
    const cpNA = outcomes.some((o) => (o.target_system_code || o.target) === "CINGLEPOINT" && o.outcome_status === "NA");
    const provDone = outcomes.length > 0
        && outcomes.every((o) => o.outcome_status === "SUCCESS" || o.outcome_status === "NA")
        && outcomes.some((o) => o.outcome_status === "SUCCESS");
    const effOk = (termOk || provDone) && !hasFailure;
    // Final step tells the truth: complete on terminal-success or all-targets-
    // done (NA counts as done); "action needed" on failure; "closed" for
    // rejected/cancelled; else "in progress".
    const finalSub = termFail ? "action needed"
        : effOk ? (cpNA && !termOk ? "complete · CP not required" : "complete")
            : termClosed ? "closed" : "in progress";

    const sec = { fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase", color: "var(--faint)", margin: "18px 0 8px" };
    const mini = { flex: 1, background: "var(--soft)", borderRadius: 10, padding: "10px 12px" };
    const miniL = { fontSize: 10, fontWeight: 700, letterSpacing: .4, textTransform: "uppercase", color: "var(--faint)" };
    const rowLine = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "1px solid var(--line)" };
    const appCard = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, background: "var(--panel, #fff)", borderRadius: 12, padding: "12px 14px", marginBottom: 8, boxShadow: "0 2px 10px rgba(20,40,90,.07)" };
    const checkSq = { width: 18, height: 18, borderRadius: 5, background: "#34d399", color: "#fff", display: "inline-grid", placeItems: "center", fontSize: 11, fontWeight: 900, flexShrink: 0 };
    const dotSt = { width: 9, height: 9, borderRadius: "50%", flexShrink: 0 };
    const statusBadge = (s) => <span className="badge" style={{ background: (SC[s] || "#6b7ba6") + "22", color: SC[s] || "#6b7ba6" }}>{s}</span>;

    // OFFBOARDING: per-system deprovisioning steps, driven by the TeamBridge
    // extract's authoritative flags (extra_attrs.offboarding_completed_*) with
    // the provisioning outcomes as fallback (covers SNS offboards, which have
    // no flags). A completed request with no failure counts all systems done.
    const ea = (D.request && (D.request.extraAttrs || D.request.extra_attrs)) || {};
    const offFlag = (k) => String(ea[k] ?? "").toLowerCase() === "true";
    const outcomeOf = (target) => outcomes.find((o) => (o.target_system_code || o.target) === target);
    const offSystems = !isOff ? [] : [
        { t: "Saviynt · DTVID deactivated", flag: "offboarding_completed_saviynt", target: "SAVIYNT" },
        { t: "Hinge · identity end-dated", flag: "offboarding_completed_hinge", target: "HINGE" },
        { t: "CinglePoint · CP agent terminated", flag: "offboarding_completed_cp", target: "CINGLEPOINT" },
    ].map((sy) => {
        const o = outcomeOf(sy.target);
        const na = o?.outcome_status === "NA";
        const okv = offFlag(sy.flag) || o?.outcome_status === "SUCCESS" || (termOk && !hasFailure && !na);
        const failed = o?.outcome_status === "FAILED" || o?.outcome_status === "RETRYING";
        return {
            t: sy.t, s: na ? "not applicable" : okv ? "complete" : failed ? "action needed" : "pending",
            ok: okv && !na, cur: !okv && !na && !failed,
            err: failed ? (o?.error_message || o?.error_code || "failed") : null
        };
    });

    const journey = [
        { t: "HINGE identity", s: (mk(masked, row.dtvid) || "—") + " · " + (mk(masked, row.attuid) || "no ATTUID"), ok: !!row.dtvid },
        { t: `Request submitted · ${String(row.source || "").toUpperCase() === "TEAMBRIDGE" ? "TB" : "S&S"}`, s: row.source_status || row.status || "—", ok: true, err: hasFailure ? failMsg : null },
        // Onboarding: app-provisioning steps. Offboarding: per-system
        // deprovisioning steps from the offboarding_completed_* flags.
        ...(isOff ? offSystems : [
            { t: `DTV apps ${appVerb} · ServiceNow`, s: dtv.length ? `${dDone}/${dtv.length} apps` : noTicketSub, ok: dtvOk, cur: dtv.length > 0 && dDone < dtv.length && !hasFailure },
            { t: "AT&T apps · myLogins", s: att.length ? `${aDone}/${att.length} confirmed` : noTicketSub, ok: attOk, cur: att.length > 0 && aDone < att.length && !hasFailure },
        ]),
        { t: termClosed ? prettyStatus(row.status) : (isOff ? "Fully offboarded" : "Fully onboarded"), s: finalSub, ok: effOk, cur: !effOk && !termFail && !termClosed },
    ];

    return (
        <>
            <div style={{ display: "flex", gap: 22, flexWrap: "wrap", padding: "4px 0 2px" }}>
                <div><div style={miniL}>DTVID · join key</div><div className="mono">{mk(masked, row.dtvid) || "—"}</div></div>
                <div><div style={miniL}>ATTUID</div><div className="mono">{mk(masked, row.attuid) || "—"}</div></div>
                <div><div style={miniL}>Submitted</div><div>{fmtDt(row.submitted_at)}</div></div>
                <div><div style={miniL}>Source</div><div>{row.source || "—"} · {snsUrl ? <a href={snsUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title="Open this nomination in the SNS portal" style={{ fontWeight: 600 }}>{displayKey}</a> : displayKey}</div></div>
                {(ea.cp_role_name || ea.cp_role_id) ? (
                    <div><div style={miniL}>CP role</div>
                        <div title={`${ea.cp_role_name || ea.cp_role_id}${ea.cp_role_id && ea.cp_role_name ? " (" + ea.cp_role_id + ")" : ""}`}
                            style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {ea.cp_role_name || ea.cp_role_id}
                        </div>
                    </div>
                ) : null}
                {apps.length > 0 && apps[0].match_method ? (
                    <div><div style={miniL}>SNOW match</div><div>{apps[0].match_method}</div></div>
                ) : null}
                {(D.request?.supersededByRehire) ? (
                    <div><div style={miniL}>Superseded</div><div><span className="badge" style={{ background: "#8593b422", color: "#8593b4" }}>rehired · new DTVID</span></div></div>
                ) : null}
            </div>

            <div style={{ display: "flex", gap: 12, marginTop: 14 }}>
                {!isOff && (<>
                    <div style={mini}><div style={miniL}>DTV apps · ServiceNow</div><div style={{ fontSize: 22, fontWeight: 800, color: "#0891b2" }}>{dtv.length ? `${dDone}/${dtv.length}` : "0"}</div></div>
                    <div style={mini}><div style={miniL}>AT&amp;T apps · myLogins</div><div style={{ fontSize: 22, fontWeight: 800, color: "#2563eb" }}>{att.length ? `${aDone}/${att.length}` : "0"}</div></div>
                </>)}
                <div style={mini}><div style={miniL}>Stage</div><div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}><span className="badge" style={{ background: (CAT_COLOR[row.status_category] || "#6b7ba6") + "22", color: CAT_COLOR[row.status_category] || "#6b7ba6", display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 7, height: 7, borderRadius: "50%", background: CAT_COLOR[row.status_category] || "#6b7ba6", flexShrink: 0 }} />{stageLabel(row)}</span>{String(ea.fte_nominated ?? "").toLowerCase() === "true" && <span className="badge" style={{ background: "#0076c022", color: "#0076c0" }} title="FTE-nominated via TeamBridge: ATTUID + Hinge only; Saviynt not applicable">DTV FTE · ATTUID+Hinge only</span>}</div></div>
            </div>

            <div style={sec}>{isOff ? "Offboarding journey" : "Onboarding journey"}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {journey.map((j) => {
                    const color = j.ok ? "#0f9d6b" : j.cur ? "#0076c0" : "#c3ccdd";
                    return (
                        <div key={j.t} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "6px 0" }}>
                            <span style={{ width: 20, height: 20, borderRadius: "50%", flex: "0 0 auto", background: j.ok ? color : "transparent", border: j.ok ? 0 : `2px solid ${color}`, color: "#fff", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>{j.ok ? "✓" : ""}</span>
                            <div><div style={{ fontWeight: 700, fontSize: 13.5 }}>{j.t}</div><div style={{ fontSize: 12, color: "var(--dim)" }}>{j.s}</div>{j.err ? <div style={{ fontSize: 12, color: "#dc3a3a", whiteSpace: "pre-wrap", marginTop: 2 }}>{j.err}</div> : null}</div>
                        </div>
                    );
                })}
            </div>

            {!isOff && dtv.length > 0 && (<>
                <div style={sec}>DTV app provisioning · ServiceNow</div>
                {dtv.map((a, i) => (
                    <div key={i} style={appCard}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                            {isDone(a) ? <span style={checkSq}>✓</span> : <span style={{ ...dotSt, background: SC[statOf(a)] || "#6b7ba6" }} />}
                            <span style={{ fontSize: 13, fontWeight: 600 }}>{a.app || a.app_name || a.app_code}</span>
                        </div>
                        <div style={{ fontSize: 12, color: "var(--faint)", whiteSpace: "nowrap" }}>
                            {isDone(a) ? "Done" : statOf(a)}{a.ritm ? <> · <span className="mono">{a.ritm}</span></> : null}
                        </div>
                    </div>
                ))}
            </>)}

            {!isOff && att.length > 0 && (<>
                <div style={sec}>AT&amp;T application confirmation · myLogins ({att.length})</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    {att.map((a, i) => (
                        <div key={i} style={{ ...appCard, marginBottom: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                                {isDone(a) ? <span style={checkSq}>✓</span> : <span style={{ ...dotSt, background: SC[statOf(a)] || "#6b7ba6" }} />}
                                <span style={{ fontSize: 12.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.app || a.app_name || a.app_code}</span>
                            </div>
                            <span style={{ fontSize: 10.5, color: "var(--faint)", whiteSpace: "nowrap" }}>{isDone(a) ? (a.note_date || "") : statOf(a)}</span>
                        </div>
                    ))}
                </div>
            </>)}

            {outcomes.length > 0 && (<>
                <div style={sec}>Provisioning outcomes</div>
                {outcomes.map((o, i) => {
                    const raw = o.outcome_status;
                    // On a rejected/cancelled (closed) request nothing will ever
                    // provision, so surface pending/in-progress systems as REJECTED
                    // rather than a misleading PENDING. SUCCESS / FAILED / NA are real
                    // historical facts and stay as-is.
                    const pendingLike = raw === "PENDING" || raw === "RETRYING" || raw === "IN_PROGRESS";
                    // OFFBOARDING harmonization: the extract's per-system completion
                    // flags (extra_attrs.offboarding_completed_*) are authoritative;
                    // if the stored outcome is a stale PENDING but the flag says the
                    // system finished (or the whole request completed cleanly), show
                    // SUCCESS — the same effective status the journey above uses.
                    // FAILED/RETRYING are never overridden.
                    const offFlagKey = { SAVIYNT: "offboarding_completed_saviynt", HINGE: "offboarding_completed_hinge", CINGLEPOINT: "offboarding_completed_cp" }[o.target_system_code || o.target];
                    const offDone = isOff && raw === "PENDING" && (offFlag(offFlagKey) || (termOk && !hasFailure));
                    // ONBOARDING harmonization: a completed request with no failure
                    // and a stale PENDING outcome (pre-existing IDs, self-service)
                    // shows SUCCESS — mirrors the offboarding rule above.
                    const onbDone = !isOff && raw === "PENDING" && termOk && !hasFailure;
                    const st = (offDone || onbDone) ? "SUCCESS" : (termClosed && pendingLike ? "REJECTED" : raw);
                    const isNA = st === "NA";
                    const isFail = st === "FAILED" || st === "RETRYING";
                    const col = st === "SUCCESS" ? "#0f9d6b" : isFail ? "#dc3a3a"
                        : st === "REJECTED" ? "#9aa7c4" : isNA ? "#8593b4" : "#0076c0";
                    return (
                        <div key={i} style={{ ...rowLine, flexDirection: "column", alignItems: "stretch", gap: 4 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div style={{ fontSize: 13 }}>{o.target_system_code || o.target}</div>
                                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                    <span className="badge" style={{ background: col + "22", color: col }}>{isNA ? "N/A" : st}</span>
                                    {isNA ? <span style={{ fontSize: 11, color: "var(--faint)" }}>{(o.target_system_code || o.target) === "SAVIYNT" && String(ea.fte_nominated ?? "").toLowerCase() === "true" ? "not required · DTV FTE" : "not required"}</span> : null}
                                    {o.error_code ? <span className="mono" style={{ fontSize: 11, color: "var(--faint)" }}>{o.error_code}</span> : null}
                                </div>
                            </div>
                            {o.error_message ? (
                                <div style={{
                                    fontSize: 12, lineHeight: 1.45, whiteSpace: "pre-wrap",
                                    color: isFail ? "#dc3a3a" : "var(--dim)"
                                }}>
                                    {masked ? "[message hidden]" : o.error_message}
                                    {!isFail ? <span style={{ color: "var(--faint)" }}> · informational — did not block this request</span> : null}
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </>)}

            {sourceMsgs.length > 0 && (<>
                <div style={sec}>Source messages</div>
                <div style={{ fontSize: 11, color: "var(--faint)", marginBottom: 8 }}>
                    reported in the source file · shown for context — classified as non-blocking / not required, so they don't count against this request
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {sourceMsgs.map((m, i) => (
                        <div key={i} style={{ borderLeft: "2px solid var(--line)", paddingLeft: 12 }}>
                            <div style={{ fontSize: 11, color: "var(--faint)", fontWeight: 700 }}>{m.label}</div>
                            <div style={{ fontSize: 12.5, marginTop: 2, whiteSpace: "pre-wrap", lineHeight: 1.45 }}>
                                {masked ? "[message hidden]" : m.text}
                            </div>
                        </div>
                    ))}
                </div>
            </>)}

            {notes.length > 0 && (<>
                <div style={{ ...sec, display: "flex", alignItems: "baseline", gap: 8 }}>
                    Work-note timeline
                    <span style={{ fontWeight: 600, textTransform: "none", letterSpacing: 0, color: "var(--faint)" }}>{notes.length} update{notes.length === 1 ? "" : "s"}</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {notes.map((e, i) => {
                        const text = cleanNote(e.note_text);
                        const st = noteStatusOf(text);
                        return (
                            <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                                {/* rail dot */}
                                <span style={{ width: 16, height: 16, borderRadius: "50%", border: `2.5px solid ${st.color}`, background: "var(--panel)", display: "inline-grid", placeItems: "center", flex: "0 0 auto", marginTop: 14 }}>
                                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: st.color }} />
                                </span>
                                {/* note card */}
                                <div style={{ flex: 1, minWidth: 0, background: "var(--panel, #fff)", borderRadius: 12, padding: "12px 14px", boxShadow: "0 2px 10px rgba(20,40,90,.07)", border: "1px solid var(--line)" }}>
                                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                                        <Avatar name={mk(masked, e.note_by) || "?"} />
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                            <div style={{ fontWeight: 800, fontSize: 12.5, textTransform: "uppercase", letterSpacing: .3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{mk(masked, e.note_by) || "—"}</div>
                                            <div style={{ fontSize: 11, color: "var(--faint)", marginTop: 1 }}>
                                                {e.note_at || "—"}{e.ritm ? <> · <span className="mono">{e.ritm}</span></> : null}
                                            </div>
                                        </div>
                                        <span className="badge" style={{ background: st.color + "22", color: st.color, flex: "0 0 auto" }}>{st.label}</span>
                                    </div>
                                    <div style={{ fontSize: 12.5, marginTop: 8, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                                        {masked ? "[note hidden]" : (text ? highlightNote(text) : "—")}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </>)}

            {events.length > 0 && (<>
                <div style={sec}>Status timeline</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {events.map((e, i) => (
                        <div key={i} style={{ borderLeft: "2px solid var(--line)", paddingLeft: 12 }}>
                            <div style={{ fontSize: 12, fontWeight: 700 }}>{e.status || e.canonical_status_code || e.note_status || "—"}{(e.status_at || e.event_at || e.date) ? " · " + (e.status_at || e.event_at || e.date) : ""}</div>
                            {(e.note || e.message) ? <div style={{ fontSize: 12, color: "var(--dim)", whiteSpace: "pre-wrap" }}>{masked ? "[note hidden]" : (e.note || e.message)}</div> : null}
                        </div>
                    ))}
                </div>
            </>)}

            <details style={{ marginTop: 18 }}>
                <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--faint)" }}>Raw record (debug)</summary>
                <pre style={{ fontSize: 11, overflow: "auto" }}>{JSON.stringify(detail, null, 2)}</pre>
            </details>
        </>
    );
}

export default function ReportingDashboard() {
    const [theme, setTheme] = useState("light");
    const [masked, setMasked] = useState(false);
    const [view, setView] = useState("overview");

    // ---- server filter params ----
    const [cohort, setCohort] = useState("");
    const [vendor, setVendor] = useState("");
    const [status, setStatus] = useState("");
    const [cat, setCat] = useState("");        // status_category filter (grouped funnel rows)
    const [qInput, setQInput] = useState("");
    const q = useDebounced(qInput, 450);
    // Submitted-date window (both optional & independent — "from" alone means
    // everything since that date; "to" alone means everything up to it). Sent as
    // from/to and applied server-side on submitted_at (buildFilters), so it
    // covers BOTH sources: SNS "Submitted Date" and TeamBridge submitted_datetime.
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [distinctOn, setDistinctOn] = useState(false);  // global: one latest record per person everywhere
    const filters = useMemo(
        () => ({
            cohort,
            partner: vendor && !vendor.startsWith("loc:") ? vendor : "",
            location: vendor.startsWith("loc:") ? vendor.slice(4) : "",
            status, category: cat, q, from: fromDate, to: toDate,
            // Unique-DTVIDs mode rides the GLOBAL filters: buildFilters is
            // shared by every endpoint, so KPIs, funnel, breakdowns, queue,
            // grid, and export all count one latest record per person.
            ...(distinctOn ? { distinct: 1 } : {}),
        }),
        [cohort, vendor, status, cat, q, fromDate, toDate, distinctOn],
    );

    // Grid-only drill (set by KPI clicks). It scopes ONLY the Agent Grid, not the
    // executive summary / KPI cards — those stay global totals. It still respects
    // the top-bar LOB / partner / search, and is auto-cleared when any global
    // filter changes (dropdowns, funnel, Clear) so the two never fight.
    const [gridDrill, setGridDrill] = useState({ status: "", category: "", label: "" });
    const gridDrillActive = !!(gridDrill.status || gridDrill.category);
    // KPI drills NARROW the page filters, never replace them: the drilled grid
    // must show exactly what the clicked KPI counted, and the KPIs are computed
    // WITH the page's status filter applied. A category drill (e.g. In flight)
    // therefore ANDs with a selected status (ONBOARDING_SUBMITTED + IN_FLIGHT
    // -> the same 2 the KPI showed). Only a status-type drill (e.g. AT_RISK)
    // replaces the page status — two statuses can't apply at once.
    const gridFilters = useMemo(
        () => {
            const base = gridDrillActive
                ? { ...filters, status: gridDrill.status || filters.status, category: gridDrill.category || filters.category }
                : filters;
            return base;   // distinct already rides `filters`
        },
        [gridDrillActive, gridDrill.status, gridDrill.category, filters],
    );

    // ---- server data ----
    const [summary, setSummary] = useState(null);
    const [funnel, setFunnel] = useState([]);
    const [lobBreak, setLobBreak] = useState([]);
    const [vendorBreak, setVendorBreak] = useState([]);
    const [failures, setFailures] = useState([]);
    const [appcov, setAppcov] = useState(null);
    const [gridRows, setGridRows] = useState([]);
    const [gridTotal, setGridTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [sort, setSort] = useState({ k: "updated_at", dir: "desc" });

    const [appWorkers, setAppWorkers] = useState({});
    const [openApp, setOpenApp] = useState(null);
    const [openCorp, setOpenCorp] = useState(null);        // partner scorecard: expanded corp
    const [openLoc, setOpenLoc] = useState(null);          // partner scorecard: expanded location
    const [locEmployees, setLocEmployees] = useState({});  // location key -> employee rows
    const [partnerTree, setPartnerTree] = useState([]);    // GET /api/partners: partner -> location tree
    const [drawer, setDrawer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [gridLoading, setGridLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    // dropdown option pools (fetched once, unfiltered, so choices never vanish)
    const [allCohorts, setAllCohorts] = useState([]);
    const [allVendors, setAllVendors] = useState([]);
    const [allLocations, setAllLocations] = useState([]);  // vendor/location names (loc: prefix in the select)
    const [allStatuses, setAllStatuses] = useState([]);

    // ---- global stats: aggregate endpoints over the FULL database ----
    const loadStats = useCallback(async (f) => {
        setLoading(true); setError(null);
        try {
            const [s, fu, lb, vb, fl, pt] = await Promise.all([
                rptSummary(f),
                rptFunnel(f),
                rptBreakdown("cohort", f),
                rptBreakdown("vendor", f),
                rptFailures(f, { limit: 40, sort: "submitted_at", dir: "asc" }),
                rptPartners(f),
            ]);
            setSummary(s); setFunnel(fu.funnel || []);
            setLobBreak(lb.rows || []); setVendorBreak(vb.rows || []);
            setFailures(fl.rows || []);
            setPartnerTree(pt.partners || []);
            rptAppCoverage({ partner: f.partner, cohort: f.cohort, q: f.q }).then(setAppcov).catch(() => setAppcov(null));
            setLastUpdated(new Date());
        } catch (e) { setError(e); }
        finally { setLoading(false); }
    }, []);

    // ---- grid: true server-side paging over the full dataset ----
    const loadGrid = useCallback(async (f, pg, so) => {
        setGridLoading(true);
        try {
            const res = await rptRequests(f, { limit: PAGE_SIZE, offset: pg * PAGE_SIZE, sort: so.k, dir: so.dir });
            setGridRows(res.rows || []); setGridTotal(res.total || 0);
        } catch (e) { setError(e); }
        finally { setGridLoading(false); }
    }, []);

    useEffect(() => { setPage(0); loadStats(filters); }, [filters, loadStats]);
    useEffect(() => { loadGrid(gridFilters, page, sort); }, [gridFilters, page, sort, loadGrid]);

    // Only the COMPETING global filters (status / funnel category) clear a KPI
    // drill — they'd otherwise fight over the same server params. LOB, partner,
    // search, and the date window all flow into gridFilters, so changing them
    // simply narrows the drilled set (e.g. "Blocked" + a date range).
    useEffect(() => { setGridDrill({ status: "", category: "", label: "" }); }, [status, cat]);

    // LOB pool: one unfiltered pass (choices never vanish)
    useEffect(() => {
        rptBreakdown("cohort", {}).then((c) => {
            setAllCohorts((c.rows || []).map((r) => r.bucket).filter((b) => b && b !== "(unknown)").sort());
        }).catch(() => { });
    }, []);

    // Status pool narrows with the LOB (same pattern as partners): only the
    // statuses that actually occur in that LOB are offered, and a selected
    // status that falls out of the narrowed list is auto-cleared.
    useEffect(() => {
        rptFunnel(cohort ? { cohort } : {}).then((f) => {
            const list = (f.funnel || []).map((r) => r.status);
            setAllStatuses(list);
            setStatus((cur) => (cur && !list.includes(cur) ? "" : cur));
        }).catch(() => { });
    }, [cohort]);

    // Partner options: SERVER-resolved companies (cleaned Master Corp Name),
    // re-fetched whenever the LOB changes so the list only shows partners that
    // actually have requests in that LOB. If the selected partner falls out of
    // the narrowed list, it is auto-cleared so the page never filters on a
    // partner that can't match.
    useEffect(() => {
        // limit=1000: the dropdown must list EVERYTHING in scope, not the
        // breakdown endpoint's chart-protecting top-100. Two dimensions load:
        // resolved COMPANIES (filter: partner=) and raw VENDOR/LOCATION names
        // (filter: location=) — so a record is findable by either name.
        Promise.all([
            rptBreakdown("partner", cohort ? { cohort, limit: 1000 } : { limit: 1000 }),
            rptBreakdown("location", cohort ? { cohort, limit: 1000 } : { limit: 1000 }),
        ]).then(([p, l]) => {
            const partners = (p.rows || []).map((r) => r.bucket).filter((b) => b && b !== "(unknown)").sort();
            const partnerSet = new Set(partners);
            // Only list locations that are NOT already a company name verbatim
            // (fallback-resolved vendors appear in both dims; no duplicates).
            const locations = (l.rows || []).map((r) => r.bucket)
                .filter((b) => b && b !== "(unknown)" && !partnerSet.has(b)).sort();
            setAllVendors(partners);
            setAllLocations(locations);
            setVendor((cur) => {
                if (!cur) return cur;
                if (cur.startsWith("loc:")) return locations.includes(cur.slice(4)) ? cur : "";
                return partnerSet.has(cur) ? cur : "";
            });
        }).catch(() => { });
    }, [cohort]);

    const refresh = () => { loadStats(filters); loadGrid(gridFilters, page, sort); };

    // ---- exec / KPI derivations (all from GLOBAL summary) ----
    const total = summary?.total || 0;
    const completed = summary?.completed || 0;
    const inflight = summary?.in_flight || 0;
    const blocked = summary?.blocked || 0;
    // "Fully provisioned" = completed end-to-end PLUS failures whose errors were
    // all cleared by the de-risking rules (benign CP, live ATTUID, orphaned
    // ssnSecId, non-blocking messages) — those are done as far as anyone is
    // concerned. In-flight / on-hold / closed requests are NOT provisioned and
    // stay in their own buckets.
    const terminalFailed = summary?.terminal_failed || 0;
    const clearedFailed = Math.max(0, terminalFailed - (summary?.failed_at_risk || 0));
    const clearedPct = terminalFailed ? Math.round((clearedFailed / terminalFailed) * 100) : 0;
    // Two-decimal precision for the executive summary sentence (the donut ring
    // and KPI chips keep integers for space; ask if you want those too).
    // Truncated to 2 decimals (never rounded up): "100.00%" is reserved for a
    // literally-complete state; 99.997% displays as 99.99%.
    const pct2 = (a, b) => (b ? (Math.floor((a / b) * 10000) / 100).toFixed(2) : "0.00");
    const clearedPct2 = terminalFailed ? (Math.floor((clearedFailed / terminalFailed) * 10000) / 100).toFixed(2) : "0.00";
    const healthy = Math.min(total, completed + clearedFailed);
    const healthyPct = pct(healthy, total);
    const healthyPct2 = pct2(healthy, total);
    // FLOOR, not round: the ring may only say 100% when blocked === 0.
    // 99.81% with 5 people blocked must read 99%, never "100% HEALTHY".
    const health = total ? Math.floor(((total - blocked) / total) * 100) : 100;
    // Ring: green is the REMAINDER (everything that is neither in flight nor
    // blocked — completed, cleared, on hold, rejected/cancelled, unmapped), so a
    // book of work with zero blockers and zero in-flight reads as a full green
    // circle. Green is computed as 100 − amber − red, so the three segments
    // always sum to exactly 100 (no grey rounding sliver).
    const gIn = pct(inflight, total), gBlk = pct(blocked, total);
    const g1 = Math.max(0, 100 - gIn - gBlk), g2 = g1 + gIn;
    const ring = `conic-gradient(var(--good) 0 ${g1}%,var(--warn) ${g1}% ${g2}%,var(--bad) ${g2}% 100%)`;
    const topTarget = (summary?.failuresByTarget || []).slice().sort((a, b) => b.affected_requests - a.affected_requests)[0];

    const landCounts = useMemo(() => {
        const o = {};
        funnel.forEach((r) => { o[r.category] = (o[r.category] || 0) + r.count; });
        return o;
    }, [funnel]);

    const threeSeg = (r) => {
        // Cleared failures (terminal_failed - failed_at_risk) count as
        // provisioned everywhere; the red segment is at-risk only.
        const atRisk = r.failed_at_risk ?? r.terminal_failed ?? 0;
        const cleared = Math.max(0, (r.terminal_failed || 0) - atRisk);
        const done = (r.completed || 0) + cleared;
        const other = Math.max(0, r.total - done - r.in_flight - atRisk);
        return [{ n: done, color: CAT_COLOR.TERMINAL_SUCCESS, t: "Provisioned" },
        { n: r.in_flight, color: CAT_COLOR.IN_FLIGHT, t: "In flight" },
        { n: atRisk, color: CAT_COLOR.TERMINAL_FAILURE, t: "At risk" },
        { n: other, color: "#9aa7c4", t: "Other" }];
    };
    const doneOf = (r) => (r.completed || 0) + Math.max(0, (r.terminal_failed || 0) - (r.failed_at_risk ?? r.terminal_failed ?? 0));
    const lobBars = useMemo(() => lobBreak.map((r) => ({ label: r.bucket, total: r.total, segs: threeSeg(r) })), [lobBreak]);
    const vendorBars = useMemo(() => vendorBreak.slice(0, 14).map((r) => ({ label: r.bucket, total: r.total, segs: threeSeg(r) })), [vendorBreak]);
    const vendorLb = useMemo(() => vendorBreak.map((r) => ({ label: r.bucket, pctDone: pct(doneOf(r), r.total), total: r.total }))
        .sort((a, b) => b.pctDone - a.pctDone || b.total - a.total), [vendorBreak]);

    // Partner scorecard data: full composition per partner, sorted two ways.
    const partnerRows = useMemo(() => vendorBreak.map((r) => ({
        label: r.bucket, total: r.total,
        completed: doneOf(r), in_flight: r.in_flight,
        other: Math.max(0, r.total - doneOf(r) - r.in_flight),
        pctDone: pct(doneOf(r), r.total),
        segs: threeSeg(r),
    })), [vendorBreak]);
    const partnersByVolume = useMemo(() => [...partnerRows].sort((a, b) => b.total - a.total), [partnerRows]);
    const partnersByCompletion = useMemo(() => [...partnerRows].sort((a, b) => b.pctDone - a.pctDone || b.total - a.total), [partnerRows]);

    // Server-resolved partner tree (/api/partners). threeSeg/doneOf read
    // completed / in_flight / terminal_failed / failed_at_risk / total, which
    // every partner and location node carries, so they feed straight in. Each
    // location keeps `code` (org_unit_code) as its stable employee-drill key.
    const corpRows = useMemo(() => partnerTree.map((p) => ({
        label: p.partner,
        total: p.total,
        completed: p.completed, in_flight: p.in_flight,
        terminal_failed: p.terminal_failed, failed_at_risk: p.failed_at_risk,
        pctDone: pct(doneOf(p), p.total),
        segs: threeSeg(p),
        locations: (p.locations || []).map((l) => ({
            label: l.location,
            code: l.orgCode,
            total: l.total,
            completed: l.completed, in_flight: l.in_flight,
            terminal_failed: l.terminal_failed, failed_at_risk: l.failed_at_risk,
            pctDone: pct(doneOf(l), l.total),
            segs: threeSeg(l),
        })),
    })), [partnerTree]);
    const corpsByVolume = useMemo(() => [...corpRows].sort((a, b) => b.total - a.total), [corpRows]);
    const corpsByCompletion = useMemo(() => [...corpRows].sort((a, b) => b.pctDone - a.pctDone || b.total - a.total), [corpRows]);

    const funnelMax = Math.max(...funnel.map((r) => r.count), 1);
    const pages = Math.max(1, Math.ceil(gridTotal / PAGE_SIZE));

    // ---- executive-summary figures (CONFIRM these field names against rptSummary) ----
    // Each has a fallback; a clause is omitted entirely when its value is null,
    // so the sentence stays clean rather than printing NaN.
    const modify = summary?.modify ?? 0; // served by /summary (MODIFY request type)
    const noTicket = summary?.no_ticket ?? summary?.no_snow_ticket ?? null;               // CONFIRM: summary.no_ticket
    const blockedOrFailed = summary?.blocked_or_failed ?? (blocked + (summary?.terminal_failed || 0)); // CONFIRM
    const topFail = summary?.topFailVendor || summary?.top_fail_vendor || null;           // CONFIRM: { name|vendor, pct }
    const topFailPct = topFail ? (topFail.pct ?? pct(topFail.affected ?? topFail.count ?? 0, blocked)) : 0;

    // App drill MUST carry the same worker-scope filters as the panel aggregate
    // (partner / cohort / q), or the worker list won't match the bar's numbers.
    const toggleApp = (code) => {
        if (openApp === code) { setOpenApp(null); return; }
        setOpenApp(code);
        if (!appWorkers[code]) {
            rptAppCoverage({ app: code, partner: filters.partner, cohort: filters.cohort, q: filters.q })
                .then((res) => setAppWorkers((m) => ({ ...m, [code]: res.workers || [] })))
                .catch(() => setAppWorkers((m) => ({ ...m, [code]: [] })));
        }
    };
    // Filters changed -> every cached per-app worker list is stale; drop the
    // cache and close any open drill so counts always match the new scope.
    useEffect(() => { setAppWorkers({}); setOpenApp(null); }, [filters]);
    // Partner scorecard drill-down. Collapsing a corp also collapses any open
    // location under it. Locations lazy-load their employees via /requests,
    // scoped to the location's exact vendor value (buildFilters matches
    // lower(w.vendor)=lower(?)), inheriting the page's other active filters.
    const toggleCorp = (corp) => {
        setOpenLoc(null);
        setOpenCorp((c) => (c === corp ? null : corp));
    };
    // `key` is the location org_unit_code when known (exact scope via ?org=),
    // else the DISPLAY LABEL, matched by the API's `location` filter — the same
    // expression the scorecard label is built from. Vendor-less rows (DirecTV
    // self-service) therefore drill correctly. Scoped to the open partner so an
    // identical label under two partners never mixes; inherits other filters.
    // `isCode` = the key is an org_unit_code (of ANY format — SNS store codes
    // are numeric, TB site/dealer ids are not) -> exact ?org= match. Only
    // code-less locations fall back to the partner + display-label filter.
    // (The old numeric-only test misrouted TB's alphanumeric codes into the
    // label filter, where they matched nothing -> "No employees".)
    const toggleLoc = (key, isCode = /^\d{6,}$/.test(String(key))) => {
        const next = openLoc === key ? null : key;
        setOpenLoc(next);
        if (next && !locEmployees[key]) {
            const scope = isCode
                ? { ...filters, org: key }
                : { ...filters, partner: openCorp || filters.partner, location: key };
            rptRequests(scope, { limit: 100, sort: "name", dir: "asc" })
                .then((res) => setLocEmployees((m) => ({ ...m, [key]: res.rows || [] })))
                .catch(() => setLocEmployees((m) => ({ ...m, [key]: [] })));
        }
    };

    // Drawer UX: Esc closes; page scroll locks while open so the panel (and its
    // close button) can never scroll out of reach behind the TB shell.
    useEffect(() => {
        if (!drawer) return;
        const onKey = (e) => { if (e.key === "Escape") setDrawer(null); };
        window.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
    }, [drawer]);

    const openDrawer = (row) => {
        setDrawer({ row, detail: null, error: null });
        rptRequestDetail(row.source, row.key)
            .then((d) => setDrawer((c) => (c && c.row === row ? { ...c, detail: d } : c)))
            .catch((e) => setDrawer((c) => (c && c.row === row ? { ...c, error: e } : c)));
    };

    // App-coverage worker rows carry dtvid/attuid but NO source/source_request_key,
    // so they can't hit /requests/{source}/{key} directly. Resolve the worker to
    // their latest request via the search endpoint (q matches dtvid/attuid/name),
    // then hand off to openDrawer. Show a provisional header immediately so the
    // panel opens without waiting on the lookup.
    const openWorkerDrawer = (w) => {
        const query = w.dtvid || w.attuid || w.id_value;
        if (!query) return;
        setDrawer({
            row: {
                first_name: w.first_name, last_name: w.last_name, dtvid: w.dtvid, attuid: w.attuid,
                cohort: w.cohort, vendor: w.vendor, key: query, status_category: "UNKNOWN",
            },
            detail: null, error: null,
        });
        rptRequests({ q: query }, { limit: 5, sort: "updated_at", dir: "desc" })
            .then((res) => {
                const rows = res.rows || [];
                const eq = (a, b) => a && b && String(a).toLowerCase() === String(b).toLowerCase();
                const match = rows.find((r) => eq(r.dtvid, w.dtvid) || eq(r.attuid, w.attuid)) || rows[0];
                if (match) openDrawer(match);
                else setDrawer((c) => (c ? { ...c, error: new Error("No onboarding request found for this worker.") } : c));
            })
            .catch((e) => setDrawer((c) => (c ? { ...c, error: e } : c)));
    };

    // server-backed export: full filtered dataset in 5k pages (capped 50k)
    // Export follows WHAT YOU ARE LOOKING AT: the drill-aware grid filters
    // (LOB / partner / status / dates / search), and on the At-Risk tab it
    // additionally scopes to the AT_RISK predicate — the exact definition the
    // High-attention KPI and tab badge count — so the file matches the page.
    const exportCsv = async () => {
        const cols = ["source", "key", "request_type", "dtvid", "attuid", "first_name", "last_name", "cohort", "partner", "vendor", "org_unit_name", "status", "status_category", "source_status",
            // app provisioning + tickets (same data the grid's meter columns show)
            "dtv_apps_done", "dtv_apps_total", "att_apps_done", "att_apps_total", "servicenow_tickets",
            "submitted_at", "completed_at", "updated_at"];
        const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
        // derived export fields from the /requests row (LATERAL app columns)
        const derive = (d) => ({
            ...d,
            dtv_apps_done: d.dtv_total != null ? (d.dtv_done ?? 0) : "",
            dtv_apps_total: d.dtv_total ?? "",
            att_apps_done: d.att_total != null ? (d.att_done ?? 0) : "",
            att_apps_total: d.att_total ?? "",
            servicenow_tickets: d.ritms || "",
        });
        const scope = view === "risk" ? { ...gridFilters, status: "AT_RISK" } : gridFilters;
        let all = [];
        for (let offset = 0; offset < 50000; offset += 5000) {
            const res = await rptRequests(scope, { limit: 5000, offset, sort: "updated_at", dir: "desc" });
            all = all.concat(res.rows || []);
            if ((res.rows || []).length < 5000) break;
        }
        const csv = [cols.join(",")].concat(all.map((d) => { const x = derive(d); return cols.map((c) => esc(x[c])).join(","); })).join("\n");
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        const slug = (v) => String(v || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 30);
        const scopeTags = [
            view === "risk" ? "at-risk" : "command-center",
            scope.cohort ? "lob-" + slug(scope.cohort) : "",
            scope.partner ? "partner-" + slug(scope.partner) : "",
            scope.status && scope.status !== "AT_RISK" ? "status-" + slug(scope.status) : "",
            scope.category ? "cat-" + slug(scope.category) : "",
        ].filter(Boolean).join("_");
        a.download = `${scopeTags}-export-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click(); URL.revokeObjectURL(a.href);
    };

    const clearFilters = () => { setCohort(""); setVendor(""); setStatus(""); setCat(""); setQInput(""); setFromDate(""); setToDate(""); setDistinctOn(false); setGridDrill({ status: "", category: "", label: "" }); setPage(0); };

    // KPI drill-down: scope ONLY the Agent Grid (leaves the summary global),
    // keep the current LOB / partner / search, reset to page 1, switch to grid.
    const drillToGrid = ({ category = "", status = "", label = "" } = {}) => {
        setGridDrill({ status, category, label });
        setPage(0);
        setView("agents");
    };
    const [t1, t2] = TITLES[view];
    const setSortK = (k) => { if (!SORTABLE[k]) return; setPage(0); setSort((s) => ({ k, dir: s.k === k && s.dir === "desc" ? "asc" : "desc" })); };

    const NAV = [
        { v: "overview", label: "Command Center" },
        { v: "agents", label: "Agent Grid", cnt: gridTotal ? gridTotal.toLocaleString() : "" },
        { v: "risk", label: "At-Risk / SLA", cnt: blocked ? blocked.toLocaleString() : "" },
        { v: "vendors", label: "Partner Scorecard" },
        { v: "apps", label: "App Coverage" },
        // Data Architecture — hidden from the tab strip (section code kept below, not shown).
        // To bring it back, uncomment the next line:
        // { v: "arch", ico: "⌥", label: "Data Architecture" },
    ];

    return (
        <AppShell>
            <div className="cc" data-theme={theme}>
                <main>
                    <header className="bar">
                        <h1>{t1} <span>· {t2}</span></h1>
                        <div className="right">
                            <span className="live"><span className="dot" />live</span>
                            <button className="hbtn" onClick={() => setTheme((t) => (t === "light" ? "dark" : "light"))} title="Toggle light/dark">Theme</button>
                            <button className={"hbtn" + (masked ? " on" : "")} onClick={() => setMasked((m) => !m)}>Mask PII</button>
                            <button className="hbtn" onClick={refresh} disabled={loading}>{loading ? "…" : "Refresh"}</button>
                        </div>
                    </header>

                    <nav className="tabs">
                        {NAV.map((n) => (
                            <button key={n.v} type="button" className={"tab" + (view === n.v ? " active" : "")} onClick={() => {
                                setView(n.v);
                                // Tab navigation always lands on the plain page filters —
                                // no auto-drill. Only an explicit KPI click applies a scope
                                // (via drillToGrid, which bypasses these buttons). The
                                // At-Risk EXPORT stays at-risk-scoped regardless: exportCsv
                                // applies status=AT_RISK by view, independent of the drill.
                                setGridDrill({ status: "", category: "", label: "" });
                                setPage(0);
                            }}>
                                {n.label}
                                {n.cnt ? <span className="cnt">{n.cnt}</span> : null}
                            </button>
                        ))}
                        <label title="One row per person — their latest success/failure record. Every number on every tab (KPIs, funnel, grid, export) counts unique DTVIDs instead of request threads."
                            style={{
                                display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", userSelect: "none",
                                padding: "5px 11px", borderRadius: 999, fontSize: 11.5, fontWeight: 700, letterSpacing: .2,
                                marginLeft: "auto", marginRight: 10, whiteSpace: "nowrap",
                                border: `1.5px solid ${distinctOn ? "var(--brand, #0076c0)" : "var(--line)"}`,
                                background: distinctOn ? "#0076c014" : "var(--panel, #fff)",
                                color: distinctOn ? "var(--brand, #0076c0)" : "var(--dim)",
                                transition: "all .15s ease",
                            }}>
                            <input type="checkbox" checked={distinctOn} onChange={(e) => { setDistinctOn(e.target.checked); setPage(0); }}
                                style={{ accentColor: "var(--brand, #0076c0)", width: 13, height: 13 }} />
                            Unique DTVIDs
                        </label>
                        <span className="tabsfoot" style={{ marginLeft: 0 }}>{lastUpdated ? "refreshed " + lastUpdated.toLocaleTimeString() : "loading…"}</span>
                    </nav>

                    <div className="content">
                        {error && <div className="err">Couldn't load reporting data — {String(error.message || error)}. Check the RPT chain and retry.</div>}

                        <div className="filters">
                            <div className="grp"><label>Line of Business</label>
                                <select value={cohort} onChange={(e) => setCohort(e.target.value)}>
                                    <option value="">All LOBs</option>{allCohorts.map((v) => <option key={v}>{v}</option>)}
                                </select></div>
                            <div className="grp"><label>Partner / Vendor</label>
                                <select value={vendor} onChange={(e) => setVendor(e.target.value)}>
                                    <option value="">All Partners</option>
                                    <optgroup label="Partners (company)">{allVendors.map((v) => <option key={v} value={v}>{v}</option>)}</optgroup>
                                    {allLocations.length > 0 && <optgroup label="Locations">{allLocations.map((v) => <option key={"loc:" + v} value={"loc:" + v}>{v}</option>)}</optgroup>}
                                </select></div>
                            <div className="grp"><label>Status</label>
                                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                                    <option value="">All Statuses</option>{allStatuses.map((v) => <option key={v}>{v}</option>)}
                                </select></div>
                            <div className="grp datewin"><label>Submitted · from / to</label>
                                <div className="daterow">
                                    <input type="date" value={fromDate} max={toDate || undefined} onChange={(e) => setFromDate(e.target.value)} title="Submitted on or after" />
                                    <span className="dsep">→</span>
                                    <input type="date" value={toDate} min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)} title="Submitted on or before" />
                                    {(fromDate || toDate) && <button type="button" className="dclr" onClick={() => { setFromDate(""); setToDate(""); }} title="Clear dates" aria-label="Clear dates">✕</button>}
                                </div></div>
                            <div className="grp" style={{ flex: 1, minWidth: 210 }}><label>Search · name · DTVID · ATTUID</label>
                                <input type="search" placeholder="e.g. Maria · DTV0481 · AT77123" value={qInput} onChange={(e) => setQInput(e.target.value)} /></div>
                            <button className="clear" onClick={clearFilters}>Clear</button>
                            <button className="hbtn expbtn" onClick={exportCsv}>⬇ Export CSV</button>
                        </div>

                        {/* ================= OVERVIEW (GLOBAL STATS) ================= */}
                        <section className={"view" + (view === "overview" ? " on" : "")}>
                            <div className="exec">
                                <div className="ring" style={{ background: ring }}><div className="rc"><b>{health}%</b><span>HEALTHY</span></div></div>
                                <div>
                                    <h3>Executive summary</h3>
                                    <p>
                                        <b>{total.toLocaleString()}</b> workforce requests (<b>{(summary?.onboarding || 0).toLocaleString()}</b> onboarding, <b>{(summary?.offboarding || 0).toLocaleString()}</b> offboarding{modify ? <>, <b>{modify.toLocaleString()}</b> modify</> : null}).
                                        {/* Per-bucket breakdown, each with its share of TOTAL (truncated 2dp): */}
                                        <b className="g">{completed.toLocaleString()} fully provisioned</b> ({pct2(completed, total)}%) ·{" "}
                                        <b className="g">{clearedFailed.toLocaleString()} partially provisioned / failures cleared</b> ({pct2(clearedFailed, total)}%) — ok to continue ·{" "}
                                        <b style={{ color: "#c98300" }}>{(summary?.in_flight || 0).toLocaleString()} in flight</b> ({pct2(summary?.in_flight || 0, total)}%){(summary?.on_hold || 0) > 0 ? <> + {(summary?.on_hold || 0).toLocaleString()} on hold ({pct2(summary?.on_hold || 0, total)}%)</> : null} ·{" "}
                                        {blocked
                                            ? <><b className="r">{blocked.toLocaleString()} high attention required</b> ({pct2(blocked, total)}%){topFail ? <> — <b>{topFail.name || topFail.vendor}</b> accounts for <b>{topFailPct}%</b></> : null}.</>
                                            : <><b className="r">0 high attention required</b> (0.00%).</>}
                                        {noTicket != null ? <> <b>{noTicket.toLocaleString()}</b> have no ServiceNow ticket.</> : null}
                                        {" "}Last 7 days: <b>{(summary?.submitted_7d || 0).toLocaleString()}</b> new submissions.
                                    </p>
                                    <Legend items={[["var(--good)", "Healthy (completed · closed · on hold)"], ["var(--warn)", "In flight"], ["var(--bad)", "High attention"]]} />
                                </div>
                            </div>
                            <div className="kgrid">
                                <Kpi l="Total requests" v={total.toLocaleString()} s={(summary?.onboarding || 0).toLocaleString() + " onboarding · " + (summary?.offboarding || 0).toLocaleString() + " offboarding" + (summary?.modify ? " · " + summary.modify.toLocaleString() + " modify" : "")} ico="▦" onClick={() => drillToGrid({})} />
                                <Kpi l="Fully provisioned" v={healthy.toLocaleString()} s={healthyPct + "% of " + total.toLocaleString() + " · " + completed.toLocaleString() + " completed + " + clearedFailed.toLocaleString() + " cleared"} vc="#0f9d6b" ico="✓" chip={clearedFailed > 0 ? `${clearedFailed.toLocaleString()} cleared (${clearedPct}%) · ok to continue` : undefined} cc="#0f9d6b" onClick={() => drillToGrid({ category: "TERMINAL_SUCCESS", label: "Completed" })} />
                                <Kpi l="In flight" v={inflight.toLocaleString()} s={"actively provisioning · " + (summary?.on_hold || 0).toLocaleString() + " on hold"} vc="#0076c0" ico="◔" onClick={() => drillToGrid({ category: "IN_FLIGHT", label: "In flight" })} />
                                <Kpi l="High attention required" v={blocked.toLocaleString()} s={"open failures — " + ((summary?.failuresByTarget || []).map((f) => `${f.target} ${f.open_failures}`).join(" · ") || "none")} vc="#dc3a3a" ico="⚠" chip="needs action" cc="#dc3a3a" onClick={() => drillToGrid({ status: "AT_RISK", label: "High attention required" })} />
                                <Kpi l="Rejected / cancelled" v={(summary?.closed_other || 0).toLocaleString()} s="rejected + cancelled by submitter · out of pipeline" vc="#9aa7c4" ico="⊘" onClick={() => drillToGrid({ category: "CLOSED_OTHER", label: "Rejected / cancelled" })} />
                            </div>
                            <div className="grid g2">
                                <div className="panel">
                                    <h3>Onboarding funnel</h3><div className="sub">requests by canonical status · click to filter everything</div>
                                    <Legend items={[[CAT_COLOR.TERMINAL_SUCCESS, "Completed"], [CAT_COLOR.IN_FLIGHT, "In flight"], [CAT_COLOR.ON_HOLD, "On hold"], [CAT_COLOR.TERMINAL_FAILURE, "At risk"], [CAT_COLOR.CLOSED_OTHER, "Other"], [CAT_COLOR.UNMAPPED, "Unmapped"]]} />
                                    <div className="funnel">
                                        {(() => {
                                            // Per-type SUBMITTED blocks (ONBOARDING/OFFBOARDING/MODIFY
                                            // _SUBMITTED, server-folded) render as their OWN bars; the
                                            // remaining IN_FLIGHT statuses (draft, info requested, ...)
                                            // still collapse into ONE "In flight / pending" bar. FAILED
                                            // sits directly under the completed blocks.
                                            const isSubmittedBar = (r) => /_SUBMITTED$/.test(r.status);
                                            const inflightRows = funnel.filter((r) => r.category === "IN_FLIGHT" && !isSubmittedBar(r));
                                            const rows = funnel.filter((r) => r.category !== "IN_FLIGHT" || isSubmittedBar(r));
                                            if (inflightRows.length) {
                                                rows.unshift({
                                                    status: "__INFLIGHT__", category: "IN_FLIGHT", group: true,
                                                    count: inflightRows.reduce((a, r) => a + r.count, 0),
                                                    title: inflightRows.map((r) => `${r.status} ${r.count.toLocaleString()}`).join(" · "),
                                                });
                                            }
                                            const isDone = (r) => /_COMPLETED$/.test(r.status) || r.status === "ACTIVE";
                                            const fi = rows.findIndex((r) => r.status === "AT_RISK" || r.status === "FAILED");
                                            const lastDone = rows.reduce((a, r, i) => (isDone(r) ? i : a), -1);
                                            if (fi >= 0 && lastDone >= 0 && fi !== lastDone + 1) {
                                                const [f] = rows.splice(fi, 1);
                                                const at = rows.reduce((a, r, i) => (isDone(r) ? i : a), -1);
                                                rows.splice(at + 1, 0, f);
                                            }
                                            return rows;
                                        })().map((r) => {
                                            const w = (r.count / funnelMax) * 100;
                                            const inBar = w > 16;
                                            const label = r.group ? "IN FLIGHT · PENDING"
                                                : r.status === "ACTIVE" ? "PROVISIONED"
                                                    : r.status === "AT_RISK" ? "HIGH ATTENTION REQUIRED"
                                                        : /_(COMPLETED|SUBMITTED)$/.test(r.status) ? r.status.replace(/_/g, " ")
                                                            : r.status;
                                            // Pseudo statuses (AT_RISK, *_COMPLETED) are resolved by the
                                            // API's status filter, so they drill by their own code.
                                            // Group bar drills via the IN_FLIGHT_PENDING pseudo-status —
                                            // in-flight MINUS the split-out submitted bars — so its click
                                            // count equals the bar (category=IN_FLIGHT would include them).
                                            const clickStatus = r.group ? "IN_FLIGHT_PENDING" : r.status;
                                            const sel = status === clickStatus;
                                            const onClick = () => { setStatus(status === clickStatus ? "" : clickStatus); setCat(""); };
                                            return (
                                                <div key={r.status} className={"frow" + (sel ? " sel" : "")}
                                                    onClick={onClick}>
                                                    <div className="fl" title={r.title || r.status}>{label}</div>
                                                    <div className="fb">
                                                        <i style={{ width: `${w}%`, background: CAT_COLOR[r.category] || "#6b7ba6" }}>
                                                            {inBar && <b className="fbn">{r.count.toLocaleString()}</b>}
                                                        </i>
                                                        {!inBar && <b className="fbn out">{r.count.toLocaleString()}</b>}
                                                    </div>
                                                    <div className="fn">{total ? Math.round((r.count / total) * 100) : 0}%</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="panel">
                                    <h3>Onboarding landing</h3><div className="sub">where all requests stand right now</div>
                                    <Donut counts={landCounts} colors={CAT_COLOR} labels={CAT_LABEL} />
                                </div>
                            </div>
                            <div className="grid g2">
                                <div className="panel">
                                    <h3>Status by LOB (cohort)</h3><div className="sub">request count by status group · full dataset</div>
                                    <Legend items={[[CAT_COLOR.TERMINAL_SUCCESS, "Completed"], [CAT_COLOR.IN_FLIGHT, "In flight"], [CAT_COLOR.TERMINAL_FAILURE, "At risk"], ["#9aa7c4", "Other (on hold / closed / unmapped)"]]} />
                                    <VBars rows={lobBars} />
                                </div>
                                <div className="panel">
                                    <h3>Application coverage</h3><div className="sub">apps seen in work notes · DTV (Saviynt) vs AT&amp;T (myLogins)</div>
                                    <Legend items={[[SC.Done, "Done"], [SC.Provisioning, "Provisioning"], [SC.Pending, "Pending"], [SC.Issue, "Issue"], [SC.Mentioned, "Mentioned"]]} />
                                    {(() => {
                                        // 48-app catalog: one long row is unreadable. Chunk into
                                        // rows of <=16 bars (2-3 rows), one VBars per row, sharing
                                        // the volume-desc order so the biggest apps lead row 1.
                                        const bars = (appcov?.rows || []).map((r) => ({
                                            label: r.app_name, total: r.total,
                                            // "done/total" where total = EVERY status (done + provisioning
                                            // + pending + issue/error + mentioned) — count(*) server-side.
                                            top: `${(r.done || 0)}/${r.total}`,
                                            topColor: (r.done || 0) >= r.total ? "#0f9d6b" : undefined,
                                            title: `${r.app_name} — Done ${r.done || 0} · Provisioning ${r.provisioning || 0} · Pending ${r.pending || 0} · Issue ${r.issue || 0} · Mentioned ${r.mentioned || 0} · Total ${r.total}`,
                                            segs: [["done", "Done"], ["provisioning", "Provisioning"], ["pending", "Pending"], ["issue", "Issue"], ["mentioned", "Mentioned"]]
                                                .map(([k, t]) => ({ n: r[k], color: SC[t], t })),
                                        }));
                                        const per = Math.ceil(bars.length / Math.min(3, Math.max(1, Math.ceil(bars.length / 16))));
                                        const rowsOf = [];
                                        for (let i = 0; i < bars.length; i += per) rowsOf.push(bars.slice(i, i + per));
                                        return rowsOf.map((chunk, i) => <VBars key={i} rows={chunk} height={190} />);
                                    })()}
                                </div>
                            </div>
                        </section>

                        {/* ================= AGENT GRID (SERVER-PAGED) ================= */}
                        <section className={"view" + (view === "agents" ? " on" : "")}>
                            {gridDrillActive && (
                                <div className="drillbar">
                                    <span className="drillchip">
                                        Filtered by KPI: <b>{gridDrill.label || gridDrill.status || gridDrill.category}</b>
                                        <button type="button" onClick={() => setGridDrill({ status: "", category: "", label: "" })} aria-label="Clear KPI filter">✕</button>
                                    </span>
                                    <span className="drillnote">summary KPIs stay global · use the filters above to narrow further</span>
                                </div>
                            )}
                            <div className="note">
                                {gridTotal.toLocaleString()} {distinctOn ? "unique DTVIDs" : "requests"} match · showing {gridTotal ? page * PAGE_SIZE + 1 : 0}–{Math.min((page + 1) * PAGE_SIZE, gridTotal)} · click a row for the full record{gridLoading ? " · loading…" : ""}
                            </div>
                            <div className="panel" style={{ padding: 0 }}>
                                <div className="tbscroll">
                                    <table id="tbl">
                                        <thead><tr>
                                            {[["name", "Agent"], ["dtvuid", "DTVID"], ["attuid", "ATTUID"], ["lob", "LOB"], ["vendor", "Partner"], ["status", "Stage"], ["type", "Type"], ["submitted_at", "Submitted"], ["dtv_apps", "DTV apps"], ["att_apps", "AT&T apps"], ["snow", "ServiceNow"]].map(([k, l]) => (
                                                <th key={k} onClick={() => setSortK(k)} style={SORTABLE[k] ? undefined : { cursor: "default" }}>
                                                    {l}{sort.k === k ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
                                                </th>
                                            ))}
                                        </tr></thead>
                                        <tbody>
                                            {gridRows.map((d) => (
                                                <tr key={d.source + d.key} onClick={() => openDrawer(d)}>
                                                    <td><span className="nmcell"><Avatar name={nm(d)} /><span className="nm">{mkn(masked, nm(d)) || "—"}</span></span></td>
                                                    <td className="mono">{mk(masked, d.dtvid)}</td>
                                                    <td className="mono">{mk(masked, d.attuid)}</td>
                                                    <td>{d.cohort}</td>
                                                    <td title={(d.partner || d.vendor || "") + (d.vendor && d.partner && d.vendor !== d.partner ? " · " + d.vendor : "")}>
                                                        {/* Resolved COMPANY — the same value the Partner filter lists,
                                                            so a row is always findable under the name shown here.
                                                            The location-grain vendor string stays as the second line. */}
                                                        <div>{d.partner || d.vendor}</div>
                                                        {d.vendor && d.partner && d.vendor !== d.partner ? <div style={{ fontSize: 10.5, color: "var(--faint)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 140 }}>{d.vendor}</div> : null}
                                                    </td>
                                                    <td><span className="badge" style={{ background: (CAT_COLOR[d.status_category] || "#6b7ba6") + "22", color: CAT_COLOR[d.status_category] || "#6b7ba6" }} title={d.status}>{stageLabel(d)}</span></td>
                                                    <td className="dtvpill">{d.request_type}</td>
                                                    <td className="mono" style={{ whiteSpace: "nowrap" }}>{d.submitted_at ? String(d.submitted_at).slice(0, 10) : "—"}</td>
                                                    <td><AppMeter done={d.dtv_done} total={d.dtv_total} palette="DTV" /></td>
                                                    <td><AppMeter done={d.att_done} total={d.att_total} palette="ATT" /></td>
                                                    <td className="mono" title={d.ritms || d.key} style={{ maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis" }}>{d.ritms || "—"}</td>
                                                </tr>
                                            ))}
                                            {!gridRows.length && <tr><td colSpan={11} className="note" style={{ padding: 16 }}>{gridLoading ? "Loading…" : "No rows match."}</td></tr>}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="pager">
                                    <span>page {page + 1} / {pages} · {gridTotal.toLocaleString()} total</span>
                                    <button disabled={page <= 0} onClick={() => setPage((p) => p - 1)}>‹ Prev</button>
                                    <button disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>Next ›</button>
                                </div>
                            </div>
                        </section>

                        {/* ================= AT-RISK / SLA (GLOBAL) ================= */}
                        <section className={"view" + (view === "risk" ? " on" : "")}>
                            <div className="kgrid" style={{ gridTemplateColumns: "repeat(4,1fr)" }}>
                                <Kpi l="High attention required" v={blocked.toLocaleString()} s="with open provisioning failures" vc="#dc3a3a" ico="⚠" onClick={() => drillToGrid({ status: "AT_RISK", label: "High attention required" })} />
                                <Kpi l="Failures cleared" v={clearedFailed.toLocaleString()} s="failed end-state, nothing blocked — ok to continue" vc="#f59e0b" ico="◑" onClick={() => drillToGrid({ category: "TERMINAL_FAILURE", label: "Failures cleared" })} />
                                <Kpi l="On hold" v={(summary?.on_hold || 0).toLocaleString()} s="paused / awaiting action" vc="#c98300" ico="⏸" onClick={() => drillToGrid({ category: "ON_HOLD", label: "On hold" })} />
                                <Kpi l="Unmapped statuses" v={(summary?.unmapped_status || 0).toLocaleString()} s="need status-map entries" vc="#7c5cd6" ico="?" />
                            </div>
                            <div className="grid g2b">
                                <div className="panel">
                                    <h3>SLA / risk health</h3><div className="sub">open failures per target system · full dataset</div>
                                    <Donut counts={Object.fromEntries((summary?.failuresByTarget || []).map((f) => [f.target, f.open_failures]))}
                                        colors={{ SAVIYNT: "#f87171", CINGLEPOINT: "#fbbf24", HINGE: "#60a5fa" }} />
                                </div>
                                <div className="panel">
                                    <h3>Escalation queue</h3><div className="sub">oldest open failures first — select to drill</div>
                                    <div className="esc">
                                        {failures.map((d, i) => (
                                            <div key={i} className="escitem" onClick={() => openDrawer(d)}>
                                                <Avatar name={nm(d)} />
                                                <div className="em">
                                                    <div className="t">{mkn(masked, nm(d)) || d.key}</div>
                                                    <div className="s">{d.vendor} · {d.target} · {d.error_code || d.outcome_status} · {d.submitted_at ? "submitted " + new Date(d.submitted_at).toLocaleDateString() : "first seen " + (d.first_attempt_at ? new Date(d.first_attempt_at).toLocaleDateString() : "—")} · {d.age_days}d old</div>
                                                </div>
                                                <span className="badge" style={{ background: "#f8717122", color: "#f87171" }}>{d.outcome_status}</span>
                                            </div>
                                        ))}
                                        {!failures.length && <div className="note">No open failures for this filter. 🎉</div>}
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* ================= PARTNER SCORECARD (GLOBAL) ================= */}
                        <section className={"view" + (view === "vendors" ? " on" : "")}>
                            <div className="grid g2b">
                                <div className="panel">
                                    <h3>Partner completion leaderboard</h3><div className="sub">by master corp · ranked by % completed · click a corp → locations → employees</div>
                                    <Legend items={[[CAT_COLOR.TERMINAL_SUCCESS, "Completed"], [CAT_COLOR.IN_FLIGHT, "In flight"], [CAT_COLOR.TERMINAL_FAILURE, "At risk"], ["#9aa7c4", "Other"]]} />
                                    <PartnerScorecard rows={corpsByCompletion} metric="pct"
                                        openCorp={openCorp} openLoc={openLoc} onToggleCorp={toggleCorp} onToggleLoc={toggleLoc}
                                        employees={locEmployees} onOpenEmployee={openDrawer} masked={masked} />
                                </div>
                                <div className="panel">
                                    <h3>Partner throughput</h3><div className="sub">by master corp · request volume · click a corp → locations → employees</div>
                                    <Legend items={[[CAT_COLOR.TERMINAL_SUCCESS, "Completed"], [CAT_COLOR.IN_FLIGHT, "In flight"], [CAT_COLOR.TERMINAL_FAILURE, "At risk"], ["#9aa7c4", "Other"]]} />
                                    <PartnerScorecard rows={corpsByVolume} metric="total"
                                        openCorp={openCorp} openLoc={openLoc} onToggleCorp={toggleCorp} onToggleLoc={toggleLoc}
                                        employees={locEmployees} onOpenEmployee={openDrawer} masked={masked} />
                                </div>
                            </div>
                        </section>

                        {/* ================= APP COVERAGE ================= */}
                        <section className={"view" + (view === "apps" ? " on" : "")}>
                            <div className="grid g2">
                                {[["DTVID", "DTV apps · Saviynt"], ["ATTUID", "AT&T apps · myLogins"]].map(([idType, title]) => {
                                    const list = (appcov?.rows || []).filter((r) => r.id_type === idType);
                                    const max = Math.max(...list.map((r) => r.total), 1);
                                    return (
                                        <div className="panel" key={idType}>
                                            <h3>{title}</h3><div className="sub">workers by app &amp; status · click an app for its workers</div>
                                            {list.map((r) => (
                                                <React.Fragment key={r.app_code}>
                                                    <div className="approw" onClick={() => toggleApp(r.app_code)}>
                                                        <div className="al">{r.app_name}</div>
                                                        <div style={{ display: "flex", height: 18, borderRadius: 6, background: "var(--soft)", overflow: "hidden" }}>
                                                            {[["done", "Done"], ["provisioning", "Provisioning"], ["pending", "Pending"], ["issue", "Issue"], ["mentioned", "Mentioned"]].map(([k, t]) => r[k] > 0 ? (
                                                                <i key={k} style={{ width: `${(r[k] / max) * 100}%`, background: SC[t], display: "block", height: "100%" }} title={`${t}: ${r[k]}`} />
                                                            ) : null)}
                                                        </div>
                                                        <div style={{ fontSize: 12, fontWeight: 700, textAlign: "right", whiteSpace: "nowrap" }}
                                                            title={`Done ${r.done || 0} · Provisioning ${r.provisioning || 0} · Pending ${r.pending || 0} · Issue ${r.issue || 0} · Mentioned ${r.mentioned || 0}`}>
                                                            <span style={{ color: (r.done || 0) >= r.total ? "#0f9d6b" : "inherit" }}>{r.done || 0}/{r.total}</span>
                                                            <span style={{ fontWeight: 500, color: "var(--faint)", marginLeft: 4 }}>provisioned</span>
                                                        </div>
                                                    </div>
                                                    {openApp === r.app_code && (
                                                        <div className="appworkers">
                                                            {!appWorkers[r.app_code] && <div className="note">Loading workers…</div>}
                                                            {appWorkers[r.app_code] && (appWorkers[r.app_code].length ? (
                                                                <table><thead><tr><th>Worker</th><th>ID</th><th>Vendor</th><th>Status</th><th>Latest note</th></tr></thead>
                                                                    <tbody>{appWorkers[r.app_code].map((w, i) => (
                                                                        <tr key={i} className="clickrow" onClick={() => openWorkerDrawer(w)} title="Open full record">
                                                                            <td>{mkn(masked, (w.first_name + " " + w.last_name).trim())}</td>
                                                                            <td className="mono">{mk(masked, w.id_value)}</td>
                                                                            <td>{w.vendor}</td>
                                                                            <td><span className="badge" style={{ background: (SC[w.note_status] || "#6b7ba6") + "22", color: SC[w.note_status] || "#6b7ba6" }}>{w.note_status}</span></td>
                                                                            <td title={w.note} style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--dim)" }}>{w.note}</td>
                                                                        </tr>
                                                                    ))}</tbody></table>
                                                            ) : <div className="note">No worker rows yet.</div>)}
                                                        </div>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                            {list.length ? <Legend items={[[SC.Done, "Done"], [SC.Provisioning, "Provisioning"], [SC.Pending, "Pending"], [SC.Issue, "Issue"], [SC.Mentioned, "Mentioned"]]} /> : null}
                                            {!list.length && <div className="note">No {title.split(" ")[0]} app data yet — run HINGE-RPT-SNOW-APP-LOADER.</div>}
                                        </div>
                                    );
                                })}
                            </div>
                        </section>

                        {/* ================= DATA ARCHITECTURE ================= */}
                        <section className={"view" + (view === "arch" ? " on" : "")}>
                            <div className="panel">
                                <h3>How the data comes together</h3><div className="sub">live counts follow the loaded data</div>
                                <div className="archflow">
                                    {[
                                        ["Sources", "SNS agent export (xlsx) · TeamBridge extract (psv) · ServiceNow RITMs + work notes (Table API)"],
                                        ["Ingestion", "S3 hinge-inbound-test-raw + loader Lambdas (SNS / TeamBridge / ServiceNow apps) → staging + etl_file_load audit"],
                                        ["Model", "dtv_hinge_rpt star schema — fct_lifecycle_request, fct_provisioning_outcome(+events), fct_worker_app_access, dims"],
                                        ["API", "HINGE-RPT-API (read-only, SQL aggregates over the full dataset) → Hinge router → MuleSoft → TB gateway"],
                                        ["This dashboard", `global stats via aggregate endpoints · grid server-paged ${PAGE_SIZE}/page over ${gridTotal.toLocaleString()} requests`],
                                    ].map(([t, d], i) => (
                                        <div className="archstage" key={t}><div className="n">{i + 1}</div><div><div className="t">{t}</div><div className="d">{d}</div></div></div>
                                    ))}
                                </div>
                            </div>
                        </section>
                    </div>
                </main>

                <div className={"scrim" + (drawer ? " on" : "")} onClick={() => setDrawer(null)} />
                <aside className={"drawer" + (drawer ? " on" : "")}>
                    {drawer && (
                        <>
                            <div className="dh" style={{ position: "relative" }}>
                                <button className="x" onClick={() => setDrawer(null)}>✕</button>
                                <div className="badge" style={{ background: (CAT_COLOR[drawer.row.status_category] || "#6b7ba6") + "22", color: CAT_COLOR[drawer.row.status_category] || "#6b7ba6", marginBottom: 8, display: "inline-flex", alignItems: "center", gap: 6 }}>
                                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: CAT_COLOR[drawer.row.status_category] || "#6b7ba6", flexShrink: 0 }} />
                                    {stageLabel(drawer.row)}
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                    <Avatar name={nm(drawer.row)} size={42} />
                                    <div>
                                        <div className="nm" style={{ textTransform: "uppercase", letterSpacing: 0.4 }}>{mkn(masked, nm(drawer.row)) || drawer.row.key}</div>
                                        <div className="mt">{[drawer.row.cohort, drawer.row.vendor].filter(Boolean).join(" · ") || drawer.row.key}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="db">
                                {drawer.error && <div className="err">Record drill-down failed upstream — showing summary from the row.</div>}
                                <DrawerBody row={drawer.row} detail={drawer.detail} masked={masked} />
                                {!drawer.detail && !drawer.error && <div className="note" style={{ marginTop: 10 }}>Loading full record…</div>}
                            </div>
                        </>
                    )}
                </aside>
            </div>
        </AppShell>
    );
}
